<script>
    import { router } from '@inertiajs/vue3'
    import NewsletterSignup from '../Components/NewsletterSignup.vue'

    // Slugs whose default title-case derivation looks wrong in SERP - extend as needed
    const breadcrumbLabelOverrides = {
        'edr': 'EDR',
        'soc': 'SOC',
        'msa': 'MSA',
        'cio': 'CIO',
        'saas-protection': 'SaaS Protection',
        'saas-alerts': 'SaaS Alerts',
        'co-managed-it': 'Co-Managed IT',
        'outsourced-cio': 'Outsourced CIO',
        'open-ai': 'OpenAI',
        '1password': '1Password',
        'unifi': 'UniFi',
        'cisco-meraki': 'Cisco Meraki',
        'autoelevate': 'AutoElevate',
        'goto': 'GoTo',
        'bitdefender': 'BitDefender',
        'it-support': 'IT Support',
        'it': 'IT',
    }

    function labelize(slug) {
        if (breadcrumbLabelOverrides[slug]) {
            return breadcrumbLabelOverrides[slug]
        }
        return slug
            .split('-')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ')
    }

    export default {
        components: {
            NewsletterSignup,
        },
        props: ['title'],
        computed: {
            canonicalUrl() {
                return 'https://inevat.com' + this.$page.url.split('?')[0];
            },
            partners() {
                return this.$page.props.partners ?? [];
            },
            partnerColumns() {
                const list = this.partners
                const half = Math.ceil(list.length / 2)
                return [list.slice(0, half), list.slice(half)]
            },
            breadcrumbSchema() {
                const path = this.$page.url.split('?')[0]
                const segments = path.split('/').filter(Boolean)
                if (segments.length === 0) {
                    return null
                }
                const items = [
                    {
                        '@type': 'ListItem',
                        position: 1,
                        name: 'Home',
                        item: 'https://inevat.com/',
                    },
                ]
                let cumulative = ''
                segments.forEach((segment, idx) => {
                    cumulative += '/' + segment
                    items.push({
                        '@type': 'ListItem',
                        position: idx + 2,
                        name: labelize(segment),
                        item: 'https://inevat.com' + cumulative,
                    })
                })
                return JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: items,
                })
            },
        },
        async mounted() {
            const { Collapse } = await import('bootstrap')
            this.removeNavigateListener = router.on('navigate', () => {
                const navbarEl = document.getElementById('navbarNav')
                if (navbarEl && navbarEl.classList.contains('show')) {
                    const collapse = Collapse.getOrCreateInstance(navbarEl)
                    collapse.hide()
                }
            })
        },
        beforeUnmount() {
            if (this.removeNavigateListener) {
                this.removeNavigateListener()
            }
        },
    }
</script>

<template>
    
    <Head>
        <link rel="canonical" :href="canonicalUrl" head-key="canonical" />
    </Head>
    <component :is="'script'" v-if="breadcrumbSchema" type="application/ld+json" v-text="breadcrumbSchema" />
    <main>

            <nav class="navbar navbar-expand-xl navbar-inevat sticky-top navbar-light bg-white px-md-5 d-print-none" id="top-nav">
                <div class="container-fluid">
                    <Link class="navbar-brand" href="/">
                        <img src="/images/inevat-logo-122.png" alt="Inevat Logo">
                    </Link>
                    
                    <div class="d-flex align-items-center d-xl-none">
                        <a href="tel:+18335069650" class="btn btn-outline-dark px-3 rounded-5 me-1">(833) 506-9650</a>
                        <Link href="/consultation" class="btn btn-dark px-4 rounded-5">Free Consultation</Link>
                        <button class="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav"
                            aria-controls="navbarNav" aria-expanded="false" aria-label="Toggle navigation">
                            <span class="navbar-toggler-icon"></span>
                        </button>
                    </div>

                    <div class="collapse navbar-collapse mt-2 mt-xl-0 ps-xl-4 text-end pe-3 pe-md-0" id="navbarNav">
                        <ul class="navbar-nav me-auto ">
                            <li class="nav-item dropdown pe-lg-4">
                                <a class="nav-link dropdown-toggle" href="#" role="button" data-bs-toggle="dropdown"
                                    aria-expanded="false">
                                    Services
                                </a>
                                <ul class="dropdown-menu dropdown-wide p-0" id="test">
                                    <div class="container-fluid pb-3 pb-md-0">
                                        <div class="row">
                                            <div class="col-12 col-md-3 bg-light-gray py-md-5 py-3">
                                                <div class="h4 fw-700">Services</div>
                                                <div class="text-secondary small fw-300 mb-3">
                                                    Enterprise security and IT services for growing businesses nationwide.
                                                </div>
                                                <li class="text-end">
                                                    <Link href="/services" class="btn btn-primary rounded-5 me-4">All Services</Link>
                                                </li>
                                                <li class="text-end mt-2">
                                                    <Link href="/pricing" class="btn btn-outline-primary rounded-5 me-4">Pricing</Link>
                                                </li>
                                                <li class="mt-3 ms-1">
                                                    <div class="h6 fw-700 text-poppins text-uppercase text-secondary small mb-2">Service Areas</div>
                                                </li>
                                                <li><Link href="/locations/national" class="dropdown-item">Nationwide</Link></li>
                                                <li><Link href="/locations/florida" class="dropdown-item">Florida</Link></li>
                                                <li><Link href="/locations/nevada" class="dropdown-item">Nevada</Link></li>
                                                <li><Link href="/locations/north-carolina" class="dropdown-item">North Carolina</Link></li>
                                                <li><Link href="/locations/utah" class="dropdown-item">Utah</Link></li>
                                            </div>
                                            <div class="col-12 col-md-3 py-md-5 py-2">
                                                <div class="h6 fw-700 ms-3 text-poppins text-uppercase text-secondary small">Security</div>
                                                <li><hr/></li>
                                                <li><Link href="/services/soc" class="dropdown-item">24x7 SOC</Link></li>
                                                <li><Link href="/services/compliance" class="dropdown-item">Compliance</Link></li>
                                                <li><Link href="/services/dark-web-monitoring" class="dropdown-item">Dark Web Monitoring</Link></li>
                                                <li><Link href="/services/edr" class="dropdown-item">EDR &amp; Antivirus</Link></li>
                                                <li><Link href="/services/email-security" class="dropdown-item">Email Security</Link></li>
                                                <li><Link href="/services/password-management" class="dropdown-item">Password Management</Link></li>
                                                <li><Link href="/services/phishing-simulation-training" class="dropdown-item">Phishing Simulation &amp; Training</Link></li>
                                                <li><Link href="/services/privilege-management" class="dropdown-item">Privilege Management</Link></li>
                                            </div>
                                            <div class="col-12 col-md-3 py-md-5 py-2">
                                                <div class="h6 fw-700 ms-3 text-poppins text-uppercase text-secondary small">Backup &amp; Protection</div>
                                                <li><hr/></li>
                                                <li><Link href="/services/file-protection" class="dropdown-item">File Protection</Link></li>
                                                <li><Link href="/services/saas-protection" class="dropdown-item">SaaS Protection</Link></li>
                                                <li><Link href="/services/saas-alerts" class="dropdown-item">SaaS Alerts</Link></li>
                                                <div class="h6 fw-700 ms-3 text-poppins text-uppercase text-secondary small mt-3">Infrastructure</div>
                                                <li><hr/></li>
                                                <li><Link href="/services/help-desk" class="dropdown-item">24/7 Help Desk</Link></li>
                                                <li><Link href="/services/it-support" class="dropdown-item">IT Support</Link></li>
                                                <li><Link href="/services/networking" class="dropdown-item">Networking</Link></li>
                                                <li><Link href="/services/wireless-design" class="dropdown-item">Wireless Design</Link></li>
                                            </div>
                                            <div class="col-12 col-md-3 py-md-5 py-2">
                                                <div class="h6 fw-700 ms-3 text-poppins text-uppercase text-secondary small">Business</div>
                                                <li><hr/></li>
                                                <li><Link href="/services/ai-consulting" class="dropdown-item">AI Consulting</Link></li>
                                                <li><Link href="/services/business-consulting" class="dropdown-item">Business Consulting</Link></li>
                                                <li><Link href="/services/co-managed-it" class="dropdown-item">Co-Managed IT</Link></li>
                                                <li><Link href="/services/integration-development" class="dropdown-item">Integration Development</Link></li>
                                                <li><Link href="/services/outsourced-cio" class="dropdown-item">Outsourced CIO</Link></li>
                                                <li><Link href="/services/project-management" class="dropdown-item">Project Management</Link></li>
                                                <li><Link href="/services/quickbooks-proadvisor" class="dropdown-item">QuickBooks ProAdvisor</Link></li>
                                                <li><Link href="/services/web-design" class="dropdown-item">Web Design</Link></li>
                                            </div>
                                        </div>
                                    </div>
                                </ul>
                            </li>
                            <li class="nav-item dropdown pe-lg-4">
                                <a class="nav-link dropdown-toggle" href="#" role="button" data-bs-toggle="dropdown"
                                    aria-expanded="false">
                                    Industries
                                </a>
                                <ul class="dropdown-menu dropdown-wide p-0 left-m-2">
                                    <div class="container-fluid pb-3 pb-md-0">
                                        <div class="row">
                                            <div class="col-12 col-md-4 col-xl-4 col-xxl-4 bg-light-gray py-md-5 py-3">
                                                <div class="h4 fw-700">Industries</div>
                                                <div class="text-secondary small fw-300 mb-4">
                                                    You don&apos;t want a generalist - we have experience in your industry.
                                                </div>
                                                <li class="text-end">
                                                    <Link href="/industries" class="btn btn-primary rounded-5 me-5">All Industries</Link>
                                                </li>
                                                <li class="mt-4 ms-1">
                                                    <div class="h6 fw-700 text-poppins text-uppercase text-secondary small mb-2">By Company Size</div>
                                                </li>
                                                <li><Link href="/small-business" class="dropdown-item">Small Business</Link></li>
                                                <li><Link href="/mid-market" class="dropdown-item">Mid-Market</Link></li>
                                                <li><Link href="/enterprise" class="dropdown-item">Enterprise &amp; Divisions</Link></li>
                                            </div>
                                            <div class="col-12 col-md-8 py-md-5 py-0 pt-2">
                                                <div class="row">
                                                    <div class="col-12">
                                                        <div class="h4 fw-700 ms-3 text-poppins">Choose your Industry</div>
                                                        <li><hr/></li>
                                                    </div>
                                                    <div class="col-12 col-md-6">
                                                        <li><Link href="/industries/accounting" class="dropdown-item">Accounting</Link></li>
                                                        <li><Link href="/industries/aerospace" class="dropdown-item">Aerospace &amp; Defense</Link></li>
                                                        <li><Link href="/industries/construction" class="dropdown-item">Construction</Link></li>
                                                        <li><Link href="/industries/dental" class="dropdown-item">Dental</Link></li>
                                                        <li><Link href="/industries/education" class="dropdown-item">Education</Link></li>
                                                        <li><Link href="/industries/financial-services" class="dropdown-item">Financial Services</Link></li>
                                                        <li><Link href="/industries/government" class="dropdown-item">Government</Link></li>
                                                    </div>
                                                    <div class="col-12 col-md-6">
                                                        <li><Link href="/industries/healthcare" class="dropdown-item">Healthcare</Link></li>
                                                        <li><Link href="/industries/legal" class="dropdown-item">Legal</Link></li>
                                                        <li><Link href="/industries/manufacturing" class="dropdown-item">Manufacturing</Link></li>
                                                        <li><Link href="/industries/nonprofit" class="dropdown-item">Nonprofit</Link></li>
                                                        <li><Link href="/industries/professional-services" class="dropdown-item">Professional Services</Link></li>
                                                        <li><Link href="/industries/real-estate" class="dropdown-item">Real Estate</Link></li>
                                                        <li><Link href="/industries/veterinary" class="dropdown-item">Veterinary</Link></li>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </ul>
                            </li>
                            <!-- <li class="nav-item dropdown pe-lg-4">
                                <a class="nav-link dropdown-toggle" href="#" role="button" data-bs-toggle="dropdown"
                                    aria-expanded="false">
                                    Resources
                                </a>
                                <ul class="dropdown-menu dropdown-wide p-0 left-m-2">
                                    <div class="container-fluid d-none d-md-block">
                                        <div class="row">
                                            <div class="col-4 col-xl-6 bg-light-gray py-5">
                                                <div class="h4 fw-700">Resources</div>
                                                <div class="text-secondary small fw-300 mb-4">
                                                    Sometimes the only thing we need is just a litte help from our friends.
                                                </div>
                                                <li>
                                                    <Link href="/resources" class="dropdown-item">Browse All Resources</Link>
                                                </li>
                                            </div>
                                            <div class="col-4  col-xl-3 py-5">
                                                <div class="h4 fw-700 ms-3 text-poppins">IT</div>
                                                <li><hr/></li>
                                                <li>
                                                    <a href="#" class="dropdown-item">Support</a>
                                                </li>
                                                <li>
                                                    <a href="#" class="dropdown-item">Consulting</a>
                                                </li>
                                                <li>
                                                    <a href="#" class="dropdown-item">Phones</a>
                                                </li>
                                                <li>
                                                    <a href="#" class="dropdown-item">Outsource CIO</a>
                                                </li>
                                               
                                            </div>
                                            <div class="col-4  col-xl-3 py-5">
                                                <div class="h4 fw-700 ms-3 text-poppins">Business</div>
                                                <li><hr/></li>
                                                <li>
                                                    <a href="#" class="dropdown-item">Consulting</a>
                                                </li>
                                                <li>
                                                    <a href="#" class="dropdown-item">Contract Negotiation</a>
                                                </li>
                                                <li>
                                                    <a href="#" class="dropdown-item">Employment</a>
                                                </li>
                                                <li>
                                                    <a href="#" class="dropdown-item">Project Management</a>
                                                </li>
                                                
                                            </div>
                                        </div>
                                        
                                        
                                    </div>
                                    
                                </ul>
                            </li> -->
                            <!-- <li class="nav-item dropdown pe-lg-4">
                                <a class="nav-link dropdown-toggle" href="#" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                                    Products
                                </a>
                                <ul class="dropdown-menu dropdown-wide p-0 left-m-2">
                                    <li><a class="dropdown-item" href="#">Action</a></li>
                                    <li><a class="dropdown-item" href="#">Another action</a></li>
                                    <li>
                                        <hr class="dropdown-divider">
                                    </li>
                                    <li><a class="dropdown-item" href="#">Something else here</a></li>
                                </ul>
                            </li> -->
                            <li class="nav-item dropdown pe-lg-4">
                                <a class="nav-link dropdown-toggle" href="#" role="button" data-bs-toggle="dropdown"
                                    aria-expanded="false">
                                    Partners
                                </a>
                                <ul class="dropdown-menu dropdown-wide p-0 left-m-3">
                                    <div class="container-fluid  pb-3 pb-md-0">
                                        <div class="row">
                                            <div class="col-12 col-md-4 col-xl-4 col-xxl-4 bg-light-gray py-md-5 py-3">
                                                <div class="h4 fw-700">Partners</div>
                                                <div class="text-secondary small fw-300 mb-4">
                                                    We can&apos;t do it all, but we have partners who can help.
                                                </div>
                                                <li class="text-end">
                                                    <Link href="/partners" class="btn btn-primary rounded-5 me-5">Meet our Partners</Link>
                                                </li>
                                            </div>
                                            <div class="col-12 col-md-8 py-md-5 py-0 pt-2">
                                                <div class="row">
                                                    <div class="col-12">
                                                        <div class="h4 fw-700 ms-3 text-poppins">Better Solutions Together With:</div>
                                                        <li><hr/></li>
                                                    </div>
                                                    <div v-for="(column, columnIndex) in partnerColumns" :key="columnIndex" class="col-12 col-md-6">
                                                        <li v-for="partner in column" :key="partner.slug">
                                                            <Link :href="`/partners/${partner.slug}`" class="dropdown-item">{{ partner.name }}</Link>
                                                        </li>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                        </div>
                                        
                                        
                                    </div>
                                    <!-- <div class="d-md-none">
                                        <li>     
                                            <a class="dropdown-item" href="#">Action</a>
                                        </li>
                                        <li><a class="dropdown-item" href="#">Another action</a></li>
                                        <li>
                                            <hr class="dropdown-divider">
                                        </li>
                                        <li><a class="dropdown-item" href="#">Something else here</a></li>
                                    </div> -->
                                    
                                </ul>
                            </li>
                            

                            <li class="nav-item pe-lg-4">
                                <Link href="/resources" class="nav-link">Resources</Link>
                            </li>

                            <li class="nav-item pe-lg-4">
                                <Link href="/contact" class="nav-link">Contact Us</Link>
                            </li>
                        </ul>
        
                        <div class="right-links ms-auto ">
                            <ul class="navbar-nav">
                                <li>
        
                                </li>
                            </ul>
        
        
                        </div>
        
                    </div>
                    <div class="d-none d-xl-flex align-items-center">

                        <a href="tel:+18335069650" class="btn btn-outline-dark px-3 rounded-5 me-2">(833) 506-9650</a>
                        <Link href="/consultation" class="btn btn-dark px-4 rounded-5">Free Consultation</Link>
                        <button class="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav"
                            aria-controls="navbarNav" aria-expanded="false" aria-label="Toggle navigation">
                            <span class="navbar-toggler-icon"></span>
                        </button>
                    </div>
                    
        
                </div>
            </nav>
        
        <slot/>

        <div class="footer bg-dark text-white d-print-none">
            <div class="py-5">
                <div class="container">

                    <div class="text-center mb-4 pb-4 border-bottom border-secondary">
                        <div class="fw-700 h5 text-uppercase text-white">IT &amp; Security Insights, Monthly</div>
                        <p class="text-secondary mb-3">Practical guidance for running a safer, smoother business. No spam, unsubscribe anytime.</p>
                        <NewsletterSignup />
                    </div>

                    <div class="text-center mb-3">
                        <div class="fw-700 h5 text-uppercase text-white">Locations</div>
                    </div>
                    <div class="row row-cols-1 row-cols-sm-2 row-cols-md-3 g-3 text-center justify-content-center mb-4">
                        <div class="col">
                            <Link href="/locations/utah" class="text-decoration-none text-secondary">
                                <div class="fw-600 text-white">Utah</div>
                                <div class="small text-secondary">Orem · Provo · Salt Lake City</div>
                            </Link>
                        </div>
                        <div class="col">
                            <Link href="/locations/north-carolina" class="text-decoration-none text-secondary">
                                <div class="fw-600 text-white">North Carolina</div>
                                <div class="small text-secondary">Charlotte · Raleigh · Triangle</div>
                            </Link>
                        </div>
                        <div class="col">
                            <Link href="/locations/nevada" class="text-decoration-none text-secondary">
                                <div class="fw-600 text-white">Nevada</div>
                                <div class="small text-secondary">Las Vegas · Henderson · Summerlin</div>
                            </Link>
                        </div>
                        <div class="col">
                            <Link href="/locations/florida" class="text-decoration-none text-secondary">
                                <div class="fw-600 text-white">Florida</div>
                                <div class="small text-secondary">Orlando · Lake Mary · Winter Park</div>
                            </Link>
                        </div>
                        <div class="col">
                            <Link href="/locations/national" class="text-decoration-none text-secondary">
                                <div class="fw-600 text-white">Nationwide</div>
                                <div class="small text-secondary">Remote-first · All 50 States</div>
                            </Link>
                        </div>
                    </div>

                    <hr class="border-secondary mb-4" />

                    <div class="row row-cols-6">
                        <div class="col-4">
                            <div class="fw-700 h5 text-uppercase">Company</div>
                            <ul class="list-unstyled text-secondary">
                                <li>
                                    <Link href="/about" class="text-decoration-none text-secondary">About</Link>
                                </li>
                                <li>
                                    <Link href="/partners" class="text-decoration-none text-secondary">Partners</Link>
                                </li>
                                <li>
                                    <Link href="/services" class="text-decoration-none text-secondary">Services</Link>
                                </li>
                                <li>
                                    <Link href="/industries" class="text-decoration-none text-secondary">Industries</Link>
                                </li>
                                <li>
                                    <Link href="/resources" class="text-decoration-none text-secondary">Resources</Link>
                                </li>
                                <li>
                                    <Link href="/pricing" class="text-decoration-none text-secondary">Pricing</Link>
                                </li>
                                <li>
                                    <Link href="/contact" class="text-decoration-none text-secondary">Contact</Link>
                                </li>
                            </ul>
                        </div>
                        <div class="col-4 text-center">
                            <div class="fw-700 h5 text-uppercase">Contact</div>
                            <ul class="list-unstyled text-secondary">
                                <li><Link href="/careers" class="link-secondary text-decoration-none">Careers</Link></li>
                                <li><Link href="/privacy" class="link-secondary text-decoration-none">Privacy</Link></li>
                                <li><Link href="/msa" class="link-secondary text-decoration-none">MSA</Link></li>
                            </ul>
                            <div class="inevat-large-a font-poppins h1 mt-4">inevāt+</div>
                            <div class="footer-phone h5"><a href="tel:+18335069650" class="text-white text-decoration-none">(833) 506-9650</a></div>
                        </div>
                        <div class="col-4 text-end">
                            <!-- <div class="fw-700 h5 text-uppercase">Services</div>
                            <ul class="list-unstyled text-secondary">
                                <li>Consulting</li>
                                <li>Contract Negotiation</li>
                                <li>Employement</li>
                                <li>IT Consulting</li>
                                <li>Project Management</li>
                            </ul> -->
                            
                            <div class="fw-700 h5 text-uppercase">Support</div>
                            <ul class="list-unstyled text-secondary">
                                <li><a href="https://cdn.inevat.com/Inevat_Support_Client.exe" target="_blank" rel="noopener noreferrer" class="link-secondary text-decoration-none">Windows Support Client</a></li>
                                <li><a href="https://cdn.inevat.com/Inevat_Support_Client.zip" target="_blank" rel="noopener noreferrer" class="link-secondary text-decoration-none">Mac Support Client</a></li>
                            </ul>
                        </div>
                    </div>

                </div>

            </div>
        </div>
    </main>
</template>

